import { FEEDBACK_UI_ATTR } from './feedback-target';

import { FEEDBACK_LIMITS } from '~/shared/utils/feedback-input';

export const SCREENSHOT_MAX_WIDTH = 1600;
export const THUMBNAIL_WIDTH = 320;

export interface EncodeAttempt {
  quality: number;
  scale: number;
}

export const SCREENSHOT_ATTEMPTS: EncodeAttempt[] = [
  { quality: 0.72, scale: 1 },
  { quality: 0.6, scale: 1 },
  { quality: 0.5, scale: 0.75 },
  { quality: 0.4, scale: 0.5 },
];

export const THUMBNAIL_ATTEMPTS: EncodeAttempt[] = [
  { quality: 0.6, scale: 1 },
  { quality: 0.45, scale: 0.75 },
];

export function scaleToFit(
  width: number,
  height: number,
  maxWidth: number
): { width: number; height: number } {
  if (width <= maxWidth) {
    return { width: Math.round(width), height: Math.round(height) };
  }
  const ratio = maxWidth / width;
  return {
    width: Math.round(maxWidth),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

export function encodeUnderCap(
  encode: (attempt: EncodeAttempt) => string,
  maxChars: number,
  attempts: EncodeAttempt[]
): string | null {
  for (const attempt of attempts) {
    const dataUrl = encode(attempt);
    if (dataUrl.length <= maxChars) {
      return dataUrl;
    }
  }
  return null;
}

function encodeCanvas(
  source: HTMLCanvasElement,
  maxWidth: number,
  attempt: EncodeAttempt
): string {
  const fitted = scaleToFit(source.width, source.height, maxWidth);
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(fitted.width * attempt.scale));
  out.height = Math.max(1, Math.round(fitted.height * attempt.scale));
  const ctx = out.getContext('2d');
  if (!ctx) {
    return '';
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(source, 0, 0, out.width, out.height);
  return out.toDataURL('image/jpeg', attempt.quality);
}

export interface ViewportCapture {
  screenshot: string | null;
  thumbnail: string | null;
}

export async function captureViewport(
  highlight: DOMRect | null
): Promise<ViewportCapture> {
  const { default: html2canvas } = await import('html2canvas');
  const canvas = await html2canvas(document.body, {
    x: window.scrollX,
    y: window.scrollY,
    width: window.innerWidth,
    height: window.innerHeight,
    scale: 1,
    useCORS: true,
    logging: false,
    ignoreElements: (el) => el.hasAttribute(FEEDBACK_UI_ATTR),
  });

  const ctx = canvas.getContext('2d');
  if (ctx && highlight && highlight.width > 0 && highlight.height > 0) {
    const ratio = canvas.width / window.innerWidth;
    ctx.strokeStyle = '#e53935';
    ctx.lineWidth = Math.max(2, Math.round(3 * ratio));
    ctx.strokeRect(
      highlight.left * ratio,
      highlight.top * ratio,
      highlight.width * ratio,
      highlight.height * ratio
    );
  }

  return {
    screenshot: encodeUnderCap(
      (attempt) => encodeCanvas(canvas, SCREENSHOT_MAX_WIDTH, attempt),
      FEEDBACK_LIMITS.screenshotChars,
      SCREENSHOT_ATTEMPTS
    ),
    thumbnail: encodeUnderCap(
      (attempt) => encodeCanvas(canvas, THUMBNAIL_WIDTH, attempt),
      FEEDBACK_LIMITS.thumbnailChars,
      THUMBNAIL_ATTEMPTS
    ),
  };
}
